import { ImageLayer, TileMapBase, TileNavigationState, TileView } from "core/tiles";
import { Material, Mesh, VertexData } from "@babylonjs/core";
import { TextureLayerView } from "./map.layer.texture";
import { ElevationLayer } from "../dem";
import { Cartesian3, IsSize } from "core/geometry";
import { ElevationGridFactory } from "./map.grid.factory";
import { TextUtils } from "core/utils";
import { Map3dMaterial } from "../materials";
import { IsHolographicBounds } from "../display";
import { ElevationLayerView } from "./map.layer.dem";
export class Map3D extends TileMapBase {
    constructor(root) {
        super();
        this._root = root;
        this._gridSize = Map3D.DefaultGridSize;
        this._offset = Cartesian3.Zero();
        this._exageration = Map3D.DefaultExageration;
        const scene = this._root.getScene();
        this._grid = this._buildTemplate(scene);
        this._grid.setEnabled(false);
        this._material = this._buildMaterial(this._buildMaterialName() ?? this.name, scene);
        if (this._material instanceof Material) {
            this._grid.material = this._material;
        }
    }
    get elevationOptions() {
        return this;
    }
    get material() {
        return this._material;
    }
    get grid() {
        return this._grid;
    }
    get name() {
        return this._root.name;
    }
    get root() {
        return this._root;
    }
    get gridSize() {
        return this._gridSize;
    }
    get gridDimension() {
        return IsSize(this._gridSize) ? Math.max(this._gridSize.width, this._gridSize.height) : this._gridSize;
    }
    set gridSize(value) {
        this._gridSize = value;
    }
    get offset() {
        return this._offset;
    }
    set offset(value) {
        this._offset = value;
    }
    get exageration() {
        return this._exageration;
    }
    set exageration(value) {
        this._exageration = value;
    }
    _buildLayerView(layer) {
        if (layer instanceof ElevationLayer) {
            const zoomOffset = this._computeTheoricalZoomOffset(this.gridDimension, layer.metrics.tileSize);
            return new ElevationLayerView(this, layer, this._display, new TileView(zoomOffset));
        }
        if (layer instanceof ImageLayer) {
            return new TextureLayerView(this, layer, this.display, this.view);
        }
        return null;
    }
    _computeTheoricalZoomOffset(gridDimension, tileSize) {
        const n = tileSize / gridDimension;
        return -Math.ceil(Math.log(n));
    }
    _onNavigationPropertyChanged(event, state) {
        super._onNavigationPropertyChanged(event, state);
        if (event.propertyName === TileNavigationState.AZIMUTH_PROPERTY_NAME) {
            this._rotateMap(event.source);
        }
    }
    _rotateMap(nav) {
        if (this._root) {
            this._root.rotation.z = this.navigationState?.azimuth.radian ?? 0;
        }
    }
    _onNavigationBinded(nav) {
        super._onNavigationBinded(nav);
        this._rotateMap(nav);
    }
    _onDisplayBinded(display) {
        super._onDisplayBinded(display);
        this._bindDisplayInternal(display);
    }
    _bindDisplayInternal(display) {
        const material = this.material;
        if (display && material && material instanceof Map3dMaterial) {
            if (IsHolographicBounds(this.display)) {
                material.holographicBounds = this.display;
            }
            if (this.display?.resolution) {
                material.displayResolution = this.display.resolution;
            }
        }
    }
    _onLayerViewAdded(eventData, eventState) {
        super._onLayerViewAdded(eventData, eventState);
        for (const v of eventData) {
            if (v instanceof TextureLayerView) {
                v.tilesRoot.parent = this._root;
                v.linkTo(this.material.imagesTarget);
                continue;
            }
            if (v instanceof ElevationLayerView) {
                v.linkTo(this.material.elevationsTarget);
                continue;
            }
        }
    }
    _onLayerViewRemoved(eventData, eventState) {
        super._onLayerViewRemoved(eventData, eventState);
        for (const v of eventData) {
            if (v instanceof TextureLayerView) {
                v.tilesRoot.parent = null;
                v.unlinkFrom(this.material.imagesTarget);
                continue;
            }
            if (v instanceof ElevationLayerView) {
                v.unlinkFrom(this.material.elevationsTarget);
            }
        }
    }
    _buildQualifiedName(n) {
        if (this.name && this.name !== "") {
            return `${this._root.name}:${n}`;
        }
        return n;
    }
    _buildTemplateName() {
        return this._buildQualifiedName(TextUtils.BuildNameWithSuffix(this.name, Map3D.TEMPLATE_SUFFIX));
    }
    _buildMaterialName() {
        return TextUtils.BuildNameWithSuffix(this._buildTemplateName(), Map3D.MATERIAL_SUFFIX);
    }
    _buildMaterial(name, scene) {
        return new Map3dMaterial(name, scene);
    }
    _buildTemplate(scene) {
        const mesh = this._buildMesh(this._buildTemplateName() ?? this.name, scene);
        const gridFactory = this._buildGridFactory() ?? this._buildGridFactoryInternal();
        const gridSize = this.gridSize ?? Map3D.DefaultGridSize;
        const grid = gridFactory.buildTopology(gridSize);
        if (grid instanceof VertexData) {
            grid.applyToMesh(mesh);
        }
        else {
            const data = new VertexData();
            data.indices = grid.indices;
            data.normals = grid.normals;
            data.positions = grid.positions;
            data.uvs = grid.uvs;
            data.applyToMesh(mesh);
        }
        return mesh;
    }
    _buildMesh(name, scene) {
        const mesh = new Mesh(name, scene);
        return mesh;
    }
    _buildGridFactory() {
        return this._buildGridFactoryInternal();
    }
    _buildGridFactoryInternal() {
        return new ElevationGridFactory();
    }
}
Map3D.DefaultGridSize = 32;
Map3D.DefaultExageration = 1.0;
Map3D.TEMPLATE_SUFFIX = "grid";
Map3D.MATERIAL_SUFFIX = "material";
//# sourceMappingURL=map.js.map